import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Pagination,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CreateGameConfig, Game, LeagueGame } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { LeagueLobby } from './LeagueLobby';
import { fetchGet } from '../../api/common';
import { Mode } from '@par/shared/common/types';
import { LeagueAdminDialog } from '../../dialog/LeagueAdminDialog';
import { StyledPaperItem } from '../../common/mui-components';
import { DbLeagueTeam } from '@par/shared/types/league';
import { CreateLeagueGameDialog } from '../../dialog/CreateLeagueGameDialog';
import {
  utcToLocalDateShort,
  utcToLocalDateTimeShort,
} from '@par/shared/utils/date-time';
import { resizeImage } from '../../api/cloudinary';
import { MAX_PLAYERS_IN_TEAMS } from '../main/LobbyTable';

export type WeekData = {
  dates: {
    start: number;
    end: number;
  };
  matches: string[];
  season: number;
  week: number;
};

type Schedule = {
  schedule: number;
  homeTeam: string;
  awayTeam: string;
  results: Record<number, { homeScore: number; awayScore: number }>;
  week: number;
};

type PropTypes = {
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handlePickTeamsClick: (gameId: string) => void;
  handleRejoinGameClick: (gameId: string) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  lobbyGamesHistory?: Game[];
  onTourneyGameCreate: (gameConfig: CreateGameConfig) => void;
  profile: Profile;
  socket: Socket;
};

export const League = ({
  handleJoinGameClick,
  handleLeaveGameClick,
  handlePickTeamsClick,
  handleSpectateClick,
  lobbyGames,
  lobbyGamesHistory,
  onTourneyGameCreate,
  profile,
  socket,
}: PropTypes) => {
  const theme = useTheme();
  const [leagueLobbyGames, setLeagueLobbyGames] = useState<Game[]>();
  const [loadingGames, setLoadingGames] = useState(true);
  const [openLeagueAdminDialog, setOpenLeagueAdminDialog] = useState(false);
  const [teams, setTeams] = useState<DbLeagueTeam[]>();
  const [openCreateLeagueGameDialog, setOpenCreateLeagueGameDialog] =
    useState(false);
  const [schedule, setSchedule] = useState<Schedule[]>();
  const [currentWeek, setCurrentWeek] = useState<number>();
  const [schedulePageNum, setSchedulePageNum] = useState<number>();
  const [lobbyPageNum, setLobbyPageNum] = useState<number>();
  const [leagueLobbyGamesPaged, setLeagueLobbyGamesPaged] = useState<Game[]>();
  const [schedulePaged, setSchedulePaged] = useState<Schedule[]>();
  const [weeks, setWeeks] = useState<WeekData[]>();
  const [openGames, setOpenGames] = useState<Game[]>();

  useEffect(() => {
    fetchAllTeams();
    fetchSchedule();
    fetchWeeks();
  }, []);

  useEffect(() => {
    if (!lobbyGames || !lobbyGamesHistory) return;
    const combinedGames = lobbyGamesHistory
      ? lobbyGamesHistory.concat(lobbyGames)
      : lobbyGames;
    setLeagueLobbyGames(
      combinedGames?.sort((a, b) => b.createdAt - a.createdAt)
    );

    const openGames = lobbyGames.filter(
      (g) =>
        g.mode === 'teams' &&
        !g.gameStarted &&
        !g.confirmingPlayers &&
        canJoin(
          g,
          profile.id,
          profile.admin && g.activePlayers.length < MAX_PLAYERS_IN_TEAMS
        )
    );
    setOpenGames(openGames);
  }, [lobbyGames, lobbyGamesHistory]);

  useEffect(() => {
    if (!schedule) return;
    const filteredSchedule = schedule.filter((w) => w.week === schedulePageNum);
    setSchedulePaged(filteredSchedule.sort((a, b) => a.schedule - b.schedule));
  }, [schedule, schedulePageNum]);

  useEffect(() => {
    if (!leagueLobbyGames) return;
    const filteredLobby = leagueLobbyGames.filter(
      (g) => g.leagueWeek === lobbyPageNum
    );
    setLeagueLobbyGamesPaged(
      filteredLobby?.sort((a, b) => a.createdAt - b.createdAt)
    );
  }, [leagueLobbyGames, lobbyPageNum]);

  const fetchAllTeams = async () => {
    const res = await fetchGet(`/league/teams/all`);
    setTeams(res.teams);
  };

  const fetchSchedule = async () => {
    const res = await fetchGet(`/league/schedule/all`);
    console.log('fetchSchedule', res);
    setSchedule(res.schedules);
  };

  const fetchWeeks = async () => {
    const res = await fetchGet('/league/weeks/all');
    setWeeks(res.weeks);
    const maxWeek = Math.max(...res.weeks.map((w) => w.week));
    setCurrentWeek(maxWeek);
    setSchedulePageNum(maxWeek);
    setLobbyPageNum(maxWeek);
  };

  const getWeekStartEndDates = (weekNum: number) => {
    if (!weeks) return null;
    const findWeek = weeks.find((w) => w.week === weekNum);
    if (!findWeek) return null;
    const startDate = utcToLocalDateShort(findWeek.dates.start);
    const endDate = utcToLocalDateShort(findWeek.dates.end);
    return `${startDate} - ${endDate}`;
  };

  const canJoin = (game: Game, profileId: string, isAdmin: boolean) => {
    if (isAdmin || !game.whoCanJoin || game.whoCanJoin.length === 0)
      return true;
    if (game.whoCanJoin?.length > 0 && game.whoCanJoin?.includes(profileId))
      return true;
    return false;
  };

  const getTeamRecord = (teamName: string) => {
    if (!teams) return null;
    const found = teams.find((t) => t.teamName === teamName);
    if (!found) return null;
    const wins = found.stats.wins;
    const losses = found.stats.losses;
    const draws = found.stats.draws;
    return `${wins}-${losses}-${draws}`;
  };

  const getTeamAvatarSrc = (teamName: string) => {
    if (!teams) return null;
    const found = teams.find((t) => t.teamName === teamName);
    if (!found) return null;
    return found.avatarPublicId;
  };

  const handleRemoveGameClick = (gameId: string) => {
    socket.emit('deleteGame', {
      profileId: profile.id,
      gameId,
    });
  };

  const handleLeagueGameCreate = (
    leagueTeamsInPlay: string[],
    leagueSeason: number,
    leagueWeek: number,
    leagueGame: number
  ) => {
    socket.emit('createEmptyLeagueGame', {
      profileId: profile.id,
      league: true,
      leagueTeamsInPlay,
      leagueSeason,
      leagueWeek,
      leagueGame,
    });
  };

  const modals = (
    <>
      {teams ? (
        <CreateLeagueGameDialog
          close={() => setOpenCreateLeagueGameDialog(false)}
          onLeagueGameCreate={handleLeagueGameCreate}
          open={openCreateLeagueGameDialog}
          teams={teams}
        />
      ) : null}
    </>
  );

  const handleSchedulePaginateOnChange = (event: any, pageNum: number) => {
    setSchedulePageNum(pageNum);
  };

  const handleLobbyPaginateOnChange = (event: any, pageNum: number) => {
    setLobbyPageNum(pageNum);
  };

  return (
    <>
      {modals}
      <Container sx={{ paddingBottom: 20 }}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={12}>
            <Typography variant="h5led" textAlign={'center'} letterSpacing={5}>
              LOUNGE
            </Typography>
          </Grid>
          <Grid item sm={12} md={12}>
            <Typography textAlign={'center'} letterSpacing={5}>
              PILOT SEASON
            </Typography>
          </Grid>
          <Grid item sm={12} md={12}>
            <Grid container spacing={3}>
              <Grid item sm={12} md={12}>
                <StyledPaperItem>
                  <Stack spacing={2}>
                    <Typography textAlign={'center'} fontWeight={600}>
                      Schedule
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="left"
                      alignItems="center"
                    >
                      {currentWeek ? (
                        <Pagination
                          count={currentWeek}
                          defaultPage={currentWeek}
                          onChange={handleSchedulePaginateOnChange}
                        />
                      ) : null}
                      <Typography textAlign={'center'} fontWeight={600}>
                        Week {schedulePageNum}
                      </Typography>
                      <Typography textAlign={'center'}>
                        ({getWeekStartEndDates(schedulePageNum)})
                      </Typography>
                    </Stack>
                    <Grid container>
                      {schedulePaged && schedulePaged.length ? (
                        schedulePaged.map((s) => (
                          <Grid item sm={12} md={6}>
                            <Stack
                              mx={1}
                              direction="row"
                              spacing={2}
                              sx={{
                                padding: 2,
                                border: '1px solid',
                                borderColor: theme.palette.primary.main,
                              }}
                            >
                              <Stack spacing={1}>
                                <Typography textAlign={'left'} color="primary">
                                  Schedule
                                </Typography>
                                {s.schedule > Date.now() ? (
                                  <>
                                    <Typography textAlign={'left'}>
                                      {
                                        utcToLocalDateTimeShort(
                                          s.schedule
                                        ).split(',')[0]
                                      }
                                      ,{' '}
                                      {
                                        utcToLocalDateTimeShort(
                                          s.schedule
                                        ).split(',')[1]
                                      }
                                    </Typography>
                                    <Typography textAlign={'left'}>
                                      {
                                        utcToLocalDateTimeShort(
                                          s.schedule
                                        ).split(',')[2]
                                      }
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography>Finished</Typography>
                                )}
                              </Stack>
                              <Stack
                                spacing={1}
                                sx={{
                                  width: '180px',
                                }}
                              >
                                <Typography textAlign={'left'} color="primary">
                                  Teams
                                </Typography>
                                <Stack
                                  direction={'row'}
                                  spacing={1}
                                  justifyContent={'left'}
                                >
                                  <Avatar
                                    src={resizeImage(
                                      getTeamAvatarSrc(s.homeTeam),
                                      50,
                                      50
                                    )}
                                    variant="rounded"
                                    sx={{
                                      height: 25,
                                      width: 25,
                                    }}
                                  />
                                  <Tooltip title={s.homeTeam} placement="top">
                                    <Typography
                                      sx={{
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {s.homeTeam}
                                    </Typography>
                                  </Tooltip>
                                </Stack>
                                <Stack
                                  direction={'row'}
                                  spacing={1}
                                  justifyContent={'left'}
                                >
                                  <Avatar
                                    src={resizeImage(
                                      getTeamAvatarSrc(s.awayTeam),
                                      50,
                                      50
                                    )}
                                    variant="rounded"
                                    sx={{
                                      height: 25,
                                      width: 25,
                                    }}
                                  />
                                  <Tooltip title={s.awayTeam} placement="top">
                                    <Typography
                                      sx={{
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {s.awayTeam}
                                    </Typography>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                              <Stack spacing={1}>
                                <Typography textAlign={'left'} color="primary">
                                  Record
                                </Typography>
                                <Typography textAlign={'left'}>
                                  {getTeamRecord(s.homeTeam)}
                                </Typography>
                                <Typography textAlign={'left'}>
                                  {getTeamRecord(s.awayTeam)}
                                </Typography>
                              </Stack>
                              {s.results &&
                                Object.values(s.results).map((r, i) => (
                                  <Stack spacing={1}>
                                    <Typography
                                      textAlign={'right'}
                                      color="primary"
                                    >
                                      G{i + 1}
                                    </Typography>
                                    <Typography
                                      textAlign={'right'}
                                      sx={{
                                        color:
                                          r.homeScore === null
                                            ? null
                                            : r.homeScore < r.awayScore
                                            ? 'success.main'
                                            : r.homeScore === r.awayScore
                                            ? null
                                            : theme.palette.demon.light,
                                      }}
                                    >
                                      {r.homeScore > 0 ? '+' : ''}
                                      {r.homeScore === null ? '-' : r.homeScore}
                                    </Typography>
                                    <Typography
                                      textAlign={'right'}
                                      sx={{
                                        color:
                                          r.awayScore === null
                                            ? null
                                            : r.awayScore < r.homeScore
                                            ? 'success.main'
                                            : r.awayScore === r.homeScore
                                            ? null
                                            : theme.palette.demon.light,
                                      }}
                                    >
                                      {r.awayScore > 0 ? '+' : ''}
                                      {r.awayScore === null ? '-' : r.awayScore}
                                    </Typography>
                                  </Stack>
                                ))}
                            </Stack>
                          </Grid>
                        ))
                      ) : (
                        <Box width="100%" mb={2}>
                          <Typography
                            textAlign={'center'}
                            sx={{ fontStyle: 'italic' }}
                          >
                            No games scheduled for week {schedulePageNum}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Stack>
                </StyledPaperItem>
              </Grid>
              <Grid item sm={12} md={12}>
                <StyledPaperItem>
                  <Stack spacing={2}>
                    <Typography textAlign={'center'} fontWeight={600}>
                      Lobby
                    </Typography>
                    {profile.admin ? (
                      <Button
                        variant="outlined"
                        onClick={() => setOpenCreateLeagueGameDialog(true)}
                      >
                        Create League Game
                      </Button>
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="left"
                      alignItems="center"
                    >
                      {currentWeek ? (
                        <Pagination
                          count={currentWeek}
                          page={lobbyPageNum}
                          onChange={handleLobbyPaginateOnChange}
                        />
                      ) : null}
                      <Typography textAlign={'center'} fontWeight={600}>
                        Week {schedulePageNum}
                      </Typography>
                      <Typography textAlign={'center'}>
                        ({getWeekStartEndDates(schedulePageNum)})
                      </Typography>
                    </Stack>
                    <Box>
                      {openGames?.length > 0 ? (
                        <Button
                          variant="outlined"
                          color="success"
                          onClick={() => setLobbyPageNum(currentWeek)}
                        >
                          Game is open - go to current week
                        </Button>
                      ) : null}
                    </Box>
                    <LeagueLobby
                      currentWeek={currentWeek}
                      handleJoinGameClick={handleJoinGameClick}
                      handleLeaveGameClick={handleLeaveGameClick}
                      handlePickTeamsClick={handlePickTeamsClick}
                      handleRemoveGameClick={handleRemoveGameClick}
                      handleSpectateClick={handleSpectateClick}
                      lobbyGames={leagueLobbyGamesPaged}
                      profile={profile}
                    />
                  </Stack>
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
